// eslint-disable-next-line no-restricted-imports -- Required for Node.js script compatibility
import { ROUTES } from '../router/const.js'
// eslint-disable-next-line no-restricted-imports -- Required for Node.js script compatibility
import { TICKET_CREATION_STEP_NAMES } from '../services/consts/ticket-creation.const.js'
// eslint-disable-next-line no-restricted-imports -- Required for Node.js script compatibility
import { DIRECTORIES } from '../services/consts/ticket.const.js'
// eslint-disable-next-line no-restricted-imports -- Required for Node.js script compatibility
import { APPROVAL_STATUS } from '../services/consts/approval.const.js'
// eslint-disable-next-line no-restricted-imports -- Required for Node.js script compatibility
import { POLICY_TYPES, POLICY_CONFIG } from '../services/consts/policy.const.js'
// eslint-disable-next-line no-restricted-imports -- Required for Node.js script compatibility
import { FAQ_TOPICS } from '../services/consts/faq-topic.const.js'
// eslint-disable-next-line no-restricted-imports -- Required for Node.js script compatibility
import { FAQ_ARTICLES } from '../services/consts/faq-articles.const.js'

export const BASE_DOMAIN = process.env.VITE_BASE_DOMAIN || process.env.BASE_DOMAIN

// Thumbnail path for Open Graph and social sharing
export const THUMBNAIL_PATH = '/thumbnail.png'

export const DEFAULT_META = {
  title: 'AI Governance Platform | Armadillex',
  description:
    // Show employees which tools they can use, request new ones and understand why something is blocked. Our portal gives your team visibility into available AI resources and appropriate use cases.
    'Discover which AI products are approved for use, request access to new ones, and understand approval decisions. Your complete portal for AI resource governance and transparency.',
  image: `${BASE_DOMAIN}${THUMBNAIL_PATH}`,
  url: BASE_DOMAIN,
}

/** Shared Help Center description for FAQ routes (static titles only). */
const HELP_CENTER_TITLE = 'Help Center'
const HELP_CENTER_DESCRIPTION =
  'Explore guides, articles, and answers about requests, approvals, confirmations, and the AI Governance Platform.'

// ============================================================================
// QUERY PARAMETER METADATA
// ============================================================================
// For drawers/modals that can appear on any route via query parameters

export const QUERY_PARAM_METADATA = {
  ticketId: {
    staticPath: '/request',
    meta: {
      title: 'Request details',
      description:
        'View AI product approval request information, details, attachments and messages.',
    },
    getDynamicMeta: (route, dynamicData) => {
      const { productName, companyName } = dynamicData
      if (productName && companyName) {
        return {
          title: `${productName} by ${companyName} | Request details `,
          description: `View approval request information, details, attachments and messages for ${productName} by ${companyName}`,
        }
      }

      return QUERY_PARAM_METADATA.ticketId.meta
    },
  },
  approvalId: {
    staticPath: '/approval',
    meta: {
      title: 'Approval details',
      description: 'View AI product approval details, usage conditions and activity.',
    },
    getDynamicMeta: (route, dynamicData) => {
      const { productName, companyName } = dynamicData

      if (productName && companyName) {
        return {
          title: `${productName} by ${companyName} | Approval details`,
          description: `View approval details, usage conditions and activity for ${productName} by ${companyName}`,
        }
      }

      return QUERY_PARAM_METADATA.approvalId.meta
    },
  },
}

/** True when a query-param overlay (ticket/approval drawer) is active. */
export function hasQueryParamOverlay(route) {
  return Object.keys(route.query || {}).some((key) => !!QUERY_PARAM_METADATA[key])
}

// ============================================================================
// ROUTES METADATA
// ============================================================================
// For rouest meta config
export const ROUTES_METADATA = {
  // Home/Dashboard
  [ROUTES.HOME]: {
    staticPath: '/app',
    meta: {
      title: 'AI Governance Dashboard',
      description:
        'View your AI product approvals dashboard, recent requests, pending confirmations and quick actions.',
    },
  },

  // Tickets/Requests Index (with status tabs)
  [ROUTES.TICKETS]: {
    staticPath: '/request',
    skipMetadataGeneration: true,
    meta: {
      title: 'Requests',
      description:
        'View your AI product usage requests, review request activity, track status and send messages to the approver.',
    },
    suffix: 'Product requests',
    subpages: [
      {
        param: DIRECTORIES.inReview,
        staticPath: `/request?status=${DIRECTORIES.inReview}`,
        title: 'Under review',
        description: `View AI product requests currently under review by your organization's approvers`,
      },
      {
        param: DIRECTORIES.approved,
        staticPath: `/request?status=${DIRECTORIES.approved}`,
        title: 'Approved',
        description: 'View approved AI product requests, their approval conditions and activity.',
      },
      {
        param: DIRECTORIES.denied,
        staticPath: `/request?status=${DIRECTORIES.denied}`,
        title: 'Denied',
        description: 'View denied AI product requests, their reasons for denial and activity.',
      },
      {
        param: DIRECTORIES.drafts,
        staticPath: `/request?status=${DIRECTORIES.drafts}`,
        title: 'Drafts',
        description: 'View and edit draft AI product requests, add details and attachments.',
      },
    ],
    defaultStatus: DIRECTORIES.inReview,
    getDynamicMeta: (route, dynamicData) => {
      const status = route.query?.status || DIRECTORIES.inReview
      const config = ROUTES_METADATA[ROUTES.TICKETS]
      const subpage = config.subpages.find((s) => s.param === status)
      const count = dynamicData?.count

      const baseMeta = subpage || config.meta
      const titlePrefix =
        count !== undefined && count > 0 ? `(${count}) ${baseMeta.title}` : baseMeta.title
      const title = subpage ? `${titlePrefix} - ${config.suffix}` : titlePrefix

      return {
        title,
        description: baseMeta.description,
      }
    },
  },

  // Approvals Index (with status tabs)
  [ROUTES.APPROVALS]: {
    staticPath: '/approval',
    skipMetadataGeneration: true,
    meta: {
      title: 'Product approvals',
      description:
        'View and manage AI product approvals, review compliance status and track product usage',
    },
    suffix: 'Product approvals',
    subpages: [
      {
        param: APPROVAL_STATUS.active,
        staticPath: `/approval?status=${APPROVAL_STATUS.active}`,
        title: 'Active',
        description: 'View active AI product approvals currently in use by your organization',
      },
      {
        param: APPROVAL_STATUS.inactive,
        staticPath: `/approval?status=${APPROVAL_STATUS.inactive}`,
        title: 'Revoked',
        description: 'View revoked AI product approvals that are no longer in use',
      },
    ],
    defaultStatus: APPROVAL_STATUS.active,
    getDynamicMeta: (route, dynamicData) => {
      const status = route.query?.status || APPROVAL_STATUS.active
      const config = ROUTES_METADATA[ROUTES.APPROVALS]
      const subpage = config.subpages.find((s) => s.param === status)
      const count = dynamicData?.count

      const baseMeta = subpage || config.meta
      const titlePrefix =
        count !== undefined && count > 0 ? `(${count}) ${baseMeta.title}` : baseMeta.title
      const title = subpage ? `${titlePrefix} - ${config.suffix}` : titlePrefix

      return {
        title,
        description: baseMeta.description,
      }
    },
  },

  // Confirmations Index
  [ROUTES.CONFIRMATIONS]: {
    staticPath: '/confirmation',
    skipMetadataGeneration: true,
    meta: {
      title: 'Product confirmations',
      description:
        'Confirm AI product usage and review products requiring periodic validation to maintain access.',
    },
    getDynamicMeta: (route, dynamicData) => {
      const { meta } = ROUTES_METADATA[ROUTES.CONFIRMATIONS]
      const count = dynamicData?.count
      const title = count !== undefined && count > 0 ? `(${count}) ${meta.title}` : meta.title

      return {
        title,
        description: meta.description,
      }
    },
  },

  // Product policies Index (with type tabs)
  [ROUTES.POLICIES]: {
    staticPath: '/policy',
    skipMetadataGeneration: true,
    meta: {
      title: 'Product policies',
      description:
        'See which products your organization allows or blocks by policy before submitting a request.',
    },
    suffix: 'Product policies',
    subpages: [
      {
        param: POLICY_TYPES.ALLOW_LIST,
        staticPath: `/policy?status=${POLICY_TYPES.ALLOW_LIST}`,
        title: POLICY_CONFIG[POLICY_TYPES.ALLOW_LIST].label,
        description: POLICY_CONFIG[POLICY_TYPES.ALLOW_LIST].metaDescription,
      },
      {
        param: POLICY_TYPES.BLOCK_LIST,
        staticPath: `/policy?status=${POLICY_TYPES.BLOCK_LIST}`,
        title: POLICY_CONFIG[POLICY_TYPES.BLOCK_LIST].label,
        description: POLICY_CONFIG[POLICY_TYPES.BLOCK_LIST].metaDescription,
      },
    ],
    defaultStatus: POLICY_TYPES.ALLOW_LIST,
    getDynamicMeta: (route, dynamicData) => {
      // Static generation passes no dynamicData and falls back to the query/default.
      const status = dynamicData?.status || route.query?.status || POLICY_TYPES.ALLOW_LIST
      const config = ROUTES_METADATA[ROUTES.POLICIES]
      const subpage = config.subpages.find((s) => s.param === status)
      const count = dynamicData?.count

      const baseMeta = subpage || config.meta
      const titlePrefix =
        count !== undefined && count > 0 ? `(${count}) ${baseMeta.title}` : baseMeta.title
      const title = subpage ? `${titlePrefix} - ${config.suffix}` : titlePrefix

      return {
        title,
        description: baseMeta.description,
      }
    },
  },

  // Edit/New Ticket (with steps)
  [ROUTES.TICKET_EDIT]: {
    staticPath: '/request/edit/:ticketStep/:ticketId?',
    meta: {
      title: 'Request approval',
      description: 'Create a new AI product approval request for your organization',
    },
    subpages: [
      {
        param: TICKET_CREATION_STEP_NAMES.PRODUCT_SEARCH,
        staticPath: `/request/edit/${TICKET_CREATION_STEP_NAMES.PRODUCT_SEARCH}/:ticketId?`,
        title: 'Request approval - Find product',
        description: 'Search for an AI product or company to create a new product approval request',
      },
      {
        param: TICKET_CREATION_STEP_NAMES.REQUEST_DETAILS,
        staticPath: `/request/edit/${TICKET_CREATION_STEP_NAMES.REQUEST_DETAILS}/:ticketId?`,
        title: 'Request approval - Edit request draft',
        description:
          'Provide details and file attachments to support your AI product approval request',
      },
      {
        param: TICKET_CREATION_STEP_NAMES.COMPLETED,
        staticPath: `/request/edit/${TICKET_CREATION_STEP_NAMES.COMPLETED}/:ticketId?`,
        title: 'Request submitted successfully',
        description: 'Your AI product approval request has been submitted and is now under review',
      },
    ],
    getDynamicMeta: (route) => {
      const step = route.params?.ticketStep || TICKET_CREATION_STEP_NAMES.PRODUCT_SEARCH
      const config = ROUTES_METADATA[ROUTES.TICKET_EDIT]
      const subpage = config.subpages.find((s) => s.param === step)

      if (subpage) {
        return {
          title: subpage.title,
          description: subpage.description,
        }
      }

      return {
        title: config.meta.title,
        description: config.meta.description,
      }
    },
  },

  // Settings — Your Profile tab
  [ROUTES.SETTINGS_DETAILS]: {
    staticPath: '/settings/details',
    meta: {
      title: 'Your Profile',
      description: 'View and manage your personal account information and statistics.',
    },
  },

  // Settings — Integrations tab
  [ROUTES.SETTINGS_INTEGRATIONS]: {
    staticPath: '/settings/integrations',
    meta: {
      title: 'Integrations',
      description:
        'Connect Armadillex to other tools to receive personal notifications about your AI product requests and approvals.',
    },
  },

  // Settings — Notifications tab
  [ROUTES.SETTINGS_NOTIFICATIONS]: {
    staticPath: '/settings/notifications',
    meta: {
      title: 'Notification settings',
      description:
        'Choose which updates on your AI product requests and approvals reach you by email and Slack - decisions, messages from your organization and approval changes.',
    },
  },

  // Help Center — landing + nested routes need `staticPath` + bot files so crawlers (Slack, etc.) get OG HTML
  [ROUTES.FAQ]: {
    staticPath: '/faq',
    meta: {
      title: HELP_CENTER_TITLE,
      description: HELP_CENTER_DESCRIPTION,
    },
  },

  [ROUTES.FAQ_TOPIC]: {
    staticPath: '/faq/:topicId',
    /** Explicit filename — pathToBaseFilename would collide with `/faq` → `faq.html` */
    botMetadataFilename: 'faq-topic.html',
    meta: {
      title: HELP_CENTER_TITLE,
      description: HELP_CENTER_DESCRIPTION,
    },
    getDynamicMeta: (route) => {
      const topic = FAQ_TOPICS.find((t) => t.id === route.params?.topicId)
      return {
        title: `${topic?.title ?? 'Topic'} | ${HELP_CENTER_TITLE}`,
        description: HELP_CENTER_DESCRIPTION,
      }
    },
  },

  [ROUTES.FAQ_ARTICLE]: {
    staticPath: '/faq/:topicId/:articleId',
    botMetadataFilename: 'faq-article.html',
    meta: {
      title: HELP_CENTER_TITLE,
      description: HELP_CENTER_DESCRIPTION,
    },
    getDynamicMeta: (route) => {
      const article = FAQ_ARTICLES[route.params?.articleId]
      return {
        title: `${article?.title ?? 'Article'} | ${HELP_CENTER_TITLE}`,
        description: HELP_CENTER_DESCRIPTION,
      }
    },
  },
}
